<template>
  <application-layout>
    <v-main>
      <v-container>
        <h1 class="mb-2">Manage Footer Navigation</h1>

        <v-btn
          data-testid="navigation-add-footer-btn"
          class="navigation-add-footer-btn mb-4 ml-0"
          @click="triggerModal()"
          color="primary"
        >
          <v-icon>add</v-icon>
          Add Footer Item
        </v-btn>

        <v-row>
          <v-col sm="6">
            <h3>Active Items</h3>

            <draggable-tree
              :data="activeItems"
              draggable="draggable"
              crossTree="navItems"
              id="active-container"
              class="mr-4 my-3"
              :class="{ empty: noneSelected }"
              @drop="addAttr($event)"
              :style="darkTheme ? darkThemeBgColor : 'background-color: white'"
            >
              <div class="px-2 ma-1" slot-scope="{data}">
                <v-row class="clickable-item">
                  <v-row class="my-3">
                    <div class="ma-2">
                      <v-icon>drag_indicator</v-icon>
                    </div>
                    <div class="mt-1">
                      <div class="title">{{ data.label }}</div>
                      <div class="caption">{{ data.url }}</div>
                    </div>
                    <v-spacer />
                    <v-icon
                      data-testid="navigation-edit-footer-btn"
                      class="navigation-edit-footer-btn mx-3"
                      @click="triggerModal(data)"
                      color="success"
                      dark
                    >
                      edit
                    </v-icon>
                  </v-row>
                </v-row>
              </div>
            </draggable-tree>
            <div class="mr-4 my-3 body-2" v-if="noneSelected">To create your custom navigation menu, drag one of the
              'Available Items' into the outlined area, or create your own custom nav item with "ADD FOOTER ITEM" above.
            </div>
          </v-col>

          <v-col sm="6">
            <h3>Available Items</h3>

            <draggable-tree
              :data="availableItems"
              draggable="draggable"
              droppable=false
              crossTree="navItems"
              id="available-container"
              class="mr-4 my-3"
              :style="darkTheme ? darkThemeBgColor : 'background-color: white'"
            >
              <div class="px-2 ma-1" slot-scope="{data}">
                <v-row class="clickable-item">
                  <v-row class="my-3">
                    <div class="ma-2">
                      <v-icon>drag_indicator</v-icon>
                    </div>
                    <div class="mt-1">
                      <div class="title">{{ data.label }}</div>
                      <div class="caption">{{ data.url }}</div>
                    </div>
                  </v-row>
                </v-row>
              </div>
            </draggable-tree>
          </v-col>
        </v-row>

        <v-btn
          data-testid="navigation-save-footer-btn"
          class="navigation-save-footer-btn ml-0 mt-2"
          color="primary"
          @click="saveFooterMenu"
        >
          Save
        </v-btn>
      </v-container>

      <v-dialog v-model="showModal" max-width="600">
        <v-card>
          <v-card-title class="headline">
            <span v-if="modalAction === 'edit'">Edit {{ modalItemData.label }}</span>
            <span v-else>Add Footer Item</span>
          </v-card-title>
          <v-divider />
          <v-card-text class="mt-5">
            <app-input
              data-testid="navigation-footer-title-input"
              class="navigation-footer-title-input"
              :type="'text'"
              label="Page Title"
              v-model="modalItemData.label"
            />
            <app-input
              data-testid="navigation-footer-url-input"
              class="navigation-footer-url-input"
              :type="'text'"
              label="Page URL"
              v-model="modalItemData.url"
            />
            <div class="subheading">Manage link attributes</div>
            <v-switch
              data-testid="navigation-footer-rel-switch"
              class="navigation-footer-rel-switch"
              :label='modalItemData.nofollow ? "rel=nofollow: Yes" : "rel=nofollow: No"'
              v-model="modalItemData.nofollow"
            />
            <v-switch
              data-testid="navigation-footer-tab-switch"
              class="navigation-footer-tab-switch mt-0"
              :label='modalItemData.new_tab ? "Open In New Tab: Yes" : "Open In New Tab: No"'
              v-model="modalItemData.new_tab"
            />
          </v-card-text>
          <v-divider />
          <v-card-actions class="justify-end">
            <v-btn
              data-testid="navigation-footer-update-link-btn"
              class="navigation-footer-update-link-btn"
              v-if="modalAction === 'edit'"
              color="primary"
              @click="updateLink"
              :disabled="!validForm"
            >
              Accept
            </v-btn>
            <v-btn
              data-testid="navigation-footer-add-link-btn"
              class="navigation-footer-add-link-btn"
              v-else
              color="primary"
              @click="addActiveLink"
              :disabled="!validForm"
            >
              Accept
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-main>
  </application-layout>
</template>

<script>
import ApplicationLayout from "@/layouts/ApplicationLayout.vue";
import { DraggableTree } from "vue-draggable-nested-tree";

export default {
  data() {
    return {
      loading: false,
      availableItems: [],
      activeItems: [],
      showModal: false,
      modalItemData: {},
      modalAction: ""
    };
  },

  components: {
    ApplicationLayout,
    DraggableTree
  },

  computed: {
    darkThemeBgColor() {
      return this.$store.state.app.darkThemeBgColor;
    },
    noneSelected() {
      return !this.activeItems.length;
    },
    validForm() {
      return this.modalItemData.label && this.modalItemData.url;
    },
    darkTheme() {
      return this.$vuetify.theme.dark;
    }
  },

  methods: {
    addAttr(data) {
      data = {
        nofollow: false,
        new_tab: false,
        ...data
      };
    },

    triggerModal(item) {
      if (item) {
        this.modalItemData = item;
        this.modalAction = "edit";
      } else {
        this.modalItemData = {};
        this.modalAction = "create";
      }
      this.showModal = true;
    },

    addActiveLink() {
      this.modalItemData.ident = this.modalItemData.label.toLowerCase().replace(" ", "-");
      this.activeItems.push(this.modalItemData);
      this.modalItemData = {};
      this.showModal = false;
    },

    updateLink() {
      this.modalItemData = {};
      this.showModal = false;
    },

    saveFooterMenu() {
      const activeItems = [];

      this.activeItems.map((item) => {
        let children = [];

        if (item.children && item.children.length) {
          children = this.handleChildren(item.children);
        }

        activeItems.push({
          ident: item.ident,
          label: item.label,
          url: item.url,
          new_tab: item.new_tab,
          nofollow: item.nofollow,
          children: children
        });
      });

      this.$store.dispatch("updateNavSetting", { ident: "footer", items: activeItems });
    },

    handleChildren(children) {
      const arr = [];

      children.map((item) => {
        let grandChildren = [];

        if (item.children && item.children.length) {
          grandChildren = this.handleChildren(item.children);
        }

        arr.push({
          ident: item.ident,
          label: item.label,
          url: item.url,
          new_tab: item.new_tab,
          nofollow: item.nofollow,
          children: grandChildren
        });
      });

      return arr;
    }
  },

  created() {
    this.loading = true;
    this.$store.dispatch("getNavSetting", "footer")
      .then(resp => {
        this.availableItems = resp.data.available ? resp.data.available : [];
        this.activeItems = resp.data.active ? resp.data.active : [];
        this.loading = false;
      })
      .catch(() => {
        this.loading = false;
      });
  }
};
</script>

<style lang="scss">
  .clickable-item {
    cursor: pointer;
  }

  .clickable-item:hover {
    color: lightblue;
    background: grey;
  }

  #active-container, #available-container {
    min-height: 5rem;
    background: #212121;
  }

  #active-container.empty, #active-container:hover {
    outline: 1px solid white;
  }
</style>
